import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Users, Crown, Calendar, Calculator, Target, Scale, Download, TrendingUp, XCircle, CheckCircle2,
} from "lucide-react";

const Stat = ({ icon: Icon, label, value, gold }) => (
  <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
    <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${gold ? "bg-[#D6A84B]/15 text-[#B8862F]" : "bg-[#EAF4FA] text-[#18A6A6]"}`}>
      <Icon className="h-5 w-5" />
    </div>
    <p className="mt-3 font-heading text-3xl font-extrabold text-[#0F2747]">{value ?? "—"}</p>
    <p className="text-sm text-slate-500">{label}</p>
  </div>
);

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");

  const loadUsers = (q = "") => api.get("/admin/users", { params: { search: q } }).then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); loadUsers(); }, []);

  const exportCsv = async () => {
    const res = await api.get("/admin/waitlist/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "career_waitlist.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#D6A84B]">Admin</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#0F2747]">App overview</h1>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} label="Total users" value={stats?.total_users} />
        <Stat icon={Users} label="Free users" value={stats?.free_users} />
        <Stat icon={Crown} label="Pro users" value={stats?.pro_users} gold />
        <Stat icon={CheckCircle2} label="Active subscriptions" value={stats?.active_subscriptions} />
        <Stat icon={Calendar} label="Monthly subscribers" value={stats?.monthly_subscribers} />
        <Stat icon={Calendar} label="Annual subscribers" value={stats?.annual_subscribers} />
        <Stat icon={XCircle} label="Canceled subscriptions" value={stats?.canceled_subscriptions} />
        <Stat icon={TrendingUp} label="Career waitlist" value={stats?.career_waitlist} gold />
        <Stat icon={Calculator} label="Shift calculations" value={stats?.shift_calculations} />
        <Stat icon={Target} label="Income plans" value={stats?.income_plans} />
        <Stat icon={Scale} label="Job comparisons" value={stats?.job_comparisons} />
      </div>

      <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-lg font-bold text-[#0F2747]">Users</h2>
          <Button onClick={exportCsv} data-testid="export-waitlist-btn" variant="outline" className="rounded-full">
            <Download className="mr-2 h-4 w-4" /> Export waitlist CSV
          </Button>
        </div>
        <div className="mt-4 flex gap-2">
          <Input data-testid="admin-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadUsers(search)} placeholder="Search by name or email" className="rounded-xl" />
          <Button data-testid="admin-search-btn" onClick={() => loadUsers(search)} className="rounded-xl bg-[#18A6A6] hover:bg-[#148f8f]">Search</Button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="py-2">Name</th><th>Email</th><th>Plan</th><th>Status</th><th>Joined</th></tr>
            </thead>
            <tbody data-testid="admin-users-table">
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-slate-100">
                  <td className="py-2.5 font-medium text-[#0F2747]">{u.first_name}</td>
                  <td className="text-slate-600">{u.email}</td>
                  <td>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${u.plan === "pro" ? "bg-[#D6A84B]/20 text-[#B8862F]" : "bg-slate-100 text-slate-600"}`}>
                      {u.plan}
                    </span>
                  </td>
                  <td className="text-slate-600">{u.status}</td>
                  <td className="text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400">No users found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
